/**
 * SWMS client sign-off data. The office reads sign-offs and links under RLS; making, sending, cancelling and the
 * executed copy go through the API's /admin routes.
 */
import { adminBlob, adminFetch } from "../../lib/api";
import { PREVIEW } from "../../lib/preview";
import { supabase } from "../../lib/supabase";

const previewOnly = (): never => {
  throw new Error("Not available in preview mode.");
};

export interface SigningLink {
  id: string; recipient_name: string; recipient_email: string; status: "sent" | "opened" | "completed" | "expired" | "cancelled";
  emailed: boolean; automatic: boolean; sent_at: string; opened_at: string | null; expires_at: string;
}
export interface SwmsSignoffRow {
  id: string; document_id: string; document_number: string; revision_label: string; issuer_name: string; issued_at_local: string;
  client_name: string | null; client_company: string | null; client_signed_at: string | null; client_signed_at_local: string | null;
  executed_pdf_path: string | null; swms_signing_requests: SigningLink[];
}

const SIGNOFF = "id, document_id, document_number, revision_label, issuer_name, issued_at_local, client_name, client_company, client_signed_at, client_signed_at_local, executed_pdf_path, swms_signing_requests(id, recipient_name, recipient_email, status, emailed, automatic, sent_at, opened_at, expires_at)";

export async function signoffsForJob(jobId: string): Promise<SwmsSignoffRow[]> {
  if (PREVIEW) return [];
  const { data, error } = await supabase.from("swms_signoffs").select(SIGNOFF).eq("job_id", jobId).order("issued_at");
  if (error) throw error;
  return data as unknown as SwmsSignoffRow[];
}

/** The client sign-off status the job shows for one SWMS version. */
export type SignoffState = { tone: "ok" | "info" | "warn"; text: string; live: SigningLink | null; last: SigningLink | null };
export function signoffState(s: SwmsSignoffRow, now = Date.now()): SignoffState {
  const links = [...s.swms_signing_requests].sort((a, b) => b.sent_at.localeCompare(a.sent_at));
  const live = links.find((l) => (l.status === "sent" || l.status === "opened") && Date.parse(l.expires_at) > now) ?? null;
  const last = links[0] ?? null;
  if (s.client_signed_at) return { tone: "ok", text: `Signed by client ${s.client_name}, ${s.client_signed_at_local}`, live: null, last };
  if (live?.status === "opened" || live?.opened_at) return { tone: "info", text: "Opened", live, last };
  if (live?.emailed) return { tone: "info", text: "Sent to client", live, last };
  return { tone: "warn", text: "Not signed", live, last };
}

export async function createSwms(jobId: string, body: { templateId: string; nearestHospital: string; highRiskWork: string[]; issuerName: string; issued: true; recipient?: { name: string; email: string } }) {
  if (PREVIEW) return previewOnly();
  return adminFetch<{ document: { id: string; versionLabel: string }; missing: string[]; replaced: number; sent: { emailed: boolean; emailError: string | null; recipientEmail: string } | null }>(
    `/jobs/${jobId}/swms`,
    { method: "POST", body: JSON.stringify(body) },
    { timeoutMs: 90_000 },
  );
}

export async function sendSwmsLink(signoffId: string, recipientName: string, recipientEmail: string) {
  if (PREVIEW) return previewOnly();
  return adminFetch<{ emailed: boolean; emailError: string | null; link: string }>(`/swms-signoffs/${signoffId}/send`, { method: "POST", body: JSON.stringify({ recipientName, recipientEmail }) });
}

export async function cancelSwmsLink(requestId: string): Promise<void> {
  if (PREVIEW) return previewOnly();
  await adminFetch(`/swms-signing-requests/${requestId}/cancel`, { method: "POST", body: "{}" });
}

export async function executedSwmsPdf(signoffId: string): Promise<Blob> {
  if (PREVIEW) return previewOnly();
  return adminBlob(`/swms-signoffs/${signoffId}/executed-pdf`, {}, { timeoutMs: 90_000 });
}

/** Who the signing link goes to by default: the job's site contact, else the client's contact. */
export async function defaultRecipient(job: { site_contact_name: string | null; site_contact_email?: string | null; customer_id?: string | null }): Promise<{ name: string; email: string }> {
  if (job.site_contact_email) return { name: job.site_contact_name ?? "", email: job.site_contact_email };
  if (PREVIEW || !job.customer_id) return { name: job.site_contact_name ?? "", email: "" };
  const { data } = await supabase.from("customers").select("contact_name, email, customer_site_contacts(name, email)").eq("id", job.customer_id).maybeSingle();
  const c = data as { contact_name: string | null; email: string | null; customer_site_contacts: { name: string; email: string | null }[] } | null;
  const site = job.site_contact_name ? c?.customer_site_contacts.find((s) => s.email && s.name.trim().toLowerCase() === job.site_contact_name!.trim().toLowerCase()) : undefined;
  if (site?.email) return { name: site.name, email: site.email };
  return { name: c?.contact_name ?? job.site_contact_name ?? "", email: c?.email ?? "" };
}

/** Today: jobs with a shift in the next 24 hours whose current SWMS the client has not signed. */
export interface UnsignedSoon { jobId: string; reference: string; title: string; firstShift: string; reason: string }
export async function unsignedSoon(now = new Date()): Promise<UnsignedSoon[]> {
  if (PREVIEW) return [];
  const until = new Date(now.getTime() + 24 * 3600_000).toISOString();
  const { data, error } = await supabase
    .from("bookings")
    .select("starts_at, status, jobs!inner(id, reference, title, status, job_documents(id, kind, superseded_by, swms_signoffs(client_signed_at)))")
    .gte("starts_at", now.toISOString())
    .lt("starts_at", until)
    .neq("status", "cancelled")
    .order("starts_at");
  if (error) throw error;
  type Doc = { id: string; kind: string; superseded_by: string | null; swms_signoffs: { client_signed_at: string | null } | { client_signed_at: string | null }[] | null };
  type Row = { starts_at: string; jobs: { id: string; reference: string; title: string; status: string; job_documents: Doc[] } };
  const out = new Map<string, UnsignedSoon>();
  for (const b of data as unknown as Row[]) {
    const j = b.jobs;
    if (!j || j.status === "cancelled" || out.has(j.id)) continue;
    const swms = j.job_documents.filter((d) => d.kind === "swms" && !d.superseded_by);
    const signed = swms.some((d) => {
      const s = Array.isArray(d.swms_signoffs) ? d.swms_signoffs[0] : d.swms_signoffs;
      return Boolean(s?.client_signed_at);
    });
    if (signed) continue;
    out.set(j.id, { jobId: j.id, reference: j.reference, title: j.title, firstShift: b.starts_at, reason: swms.length ? "SWMS not signed by the client" : "No SWMS yet" });
  }
  return [...out.values()];
}
